import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { OperationsViewModel } from "../../features/operations/operations-viewmodel";
import { UserIdViewModel } from "../../features/user-id/user-id-viewmodel";
import { AppRoutes } from "../../routes/routes";
import { formatTimestamp } from "../../utils/formatters";
import { useViewModel } from "../../viewmodel/use-view-model";
import { WriteAccessAction } from "../access/role-access";
import { Breadcrumb } from "../breadcrumb";
import { EmptyView } from "../empty/EmptyView";
import { RowKeyVal } from "../table/RowKeyVal";
import { bsClassForOperationStatus } from "./op-bs-mapping";
import { OperationDetailModalView } from "./operation-detail-modal-view";
export const OperationDetailView = observer(() => {
    const vm = useViewModel(OperationsViewModel);
    const userVM = useViewModel(UserIdViewModel);
    const { t } = useTranslation();
    const hasParamSection = Boolean(vm.selectedOperation?.parameters
        && Object.keys(vm.selectedOperation.parameters).length > 0);
    const hasAdditionalSection = Boolean(vm.selectedOperation?.additionalData
        && Object.keys(vm.selectedOperation.additionalData).length > 0);
    const onCancel = async (reason) => {
        await vm.cancelOperation(vm.selectedOperation, userVM.userId, reason);
    };
    const tHeaders = useMemo(() => ({
        userId: t("operations.detail.userId"),
        externalUserId: t("operations.detail.externalUserId"),
        status: t("operations.detail.status"),
        reason: t("operations.detail.reason"),
        type: t("operations.detail.type"),
        template: t("operations.detail.template"),
        failureCount: t("operations.detail.failureCount"),
        maxFailureCount: t("operations.detail.maxFailureCount"),
        flag: t("operations.detail.flag"),
        proximityOtp: t("operations.detail.proximityOtp"),
        created: t("operations.detail.created"),
        expires: t("operations.detail.expires"),
        finalized: t("operations.detail.finalized"),
        registration: t("operations.detail.registration")
    }), [t]);
    const handleReloadOperation = async () => await vm.reloadSelectedOperation();
    if (vm.selectedOperation === null) {
        return (_jsx(EmptyView, { message: _jsx(Trans, { i18nKey: "operations.detail.notFound", children: _jsx(Link, { to: AppRoutes.Operations }) }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(Breadcrumb, {}), _jsxs("div", { className: "card mb-3", children: [_jsxs("div", { className: "card-header pt-3 pb-3 d-flex justify-content-between align-items-center", children: [_jsxs("div", { children: [_jsxs("span", { className: "fw-semibold userSelectDisabled", children: [t("operations.detail.operation"), ": "] }), _jsx("span", { className: "font-monospace userSelectEnabled", children: vm.selectedOperation.operationId })] }), _jsxs("button", { className: "btn btn-outline-secondary btn-sm", onClick: () => handleReloadOperation(), children: [_jsx("i", { className: "bi bi-arrow-clockwise me-1" }), t("userTabs.reload")] })] }), _jsx("div", { className: "card-body", children: renderDetail(vm.selectedOperation, tHeaders, t) }), vm.canCancelOperation &&
                        _jsxs("div", { className: "card-footer d-flex justify-content-between", children: [_jsx("div", {}), _jsx("div", { className: "d-flex", children: _jsx(WriteAccessAction, { children: _jsx("button", { type: "button", className: "btn btn-danger btn-sm", "data-bs-toggle": "modal", "data-bs-target": "#opDetailModal", children: t("operations.cancelOperation") }) }) })] })] }), hasParamSection &&
                _jsxs("div", { className: "border rounded mb-3", children: [_jsx("h5", { className: "m-3", children: t("operations.detail.parameters") }), _jsx("div", { className: "mb-3", children: renderParams(vm.selectedOperation.parameters) })] }), hasAdditionalSection &&
                _jsxs("div", { className: "border rounded mb-3", children: [_jsx("h5", { className: "m-3", children: t("operations.detail.additionalAttributes") }), _jsx("div", { className: "mb-3", children: renderAdditional(vm.selectedOperation.additionalData) })] }), _jsx(OperationDetailModalView, { modalId: "opDetailModal", onCancel: onCancel })] }));
});
function renderDetail(op, tHeaders, t) {
    const statusElement = _jsx("span", { className: `badge ${bsClassForOperationStatus(op)}`, children: t(`operations.status.${op.status}`) });
    return _jsxs("div", { className: "card-text", children: [_jsx(RowKeyVal, { arr: [
                    { key: tHeaders.userId, val: op.userId },
                    { key: tHeaders.externalUserId, val: op.externalId }
                ] }), _jsx(RowKeyVal, { arr: [
                    { key: tHeaders.status, elementVal: statusElement },
                    { key: tHeaders.reason, val: op.statusReason }
                ] }), _jsx(RowKeyVal, { arr: [
                    { key: tHeaders.type, val: op.operationType },
                    { key: tHeaders.template, val: op.template }
                ] }), _jsx(RowKeyVal, { arr: [
                    { key: tHeaders.failureCount, val: String(op.failureCount) },
                    { key: tHeaders.maxFailureCount, val: String(op.maxFailureCount) }
                ] }), _jsx(RowKeyVal, { arr: [
                    { key: tHeaders.flag, val: op.flag },
                    { key: tHeaders.proximityOtp, val: op.proximityOtp }
                ] }), _jsx(RowKeyVal, { arr: [
                    { key: tHeaders.created, val: formatTimestamp(op.timestampCreated) },
                    { key: tHeaders.expires, val: formatTimestamp(op.timestampExpires) }
                ] }), _jsx(RowKeyVal, { arr: [
                    { key: tHeaders.finalized, val: formatTimestamp(op.timestampFinalized) },
                    { key: tHeaders.registration, val: op.registrationId }
                ] })] });
}
function renderParams(params) {
    if (!params) {
        return _jsx(_Fragment, {});
    }
    return (_jsx("div", { className: "ps-3 pe-3", children: params && Object.entries(params).map(([key, value]) => (_jsxs("div", { children: [_jsxs("span", { className: "fw-bold", children: [key, ": "] }), _jsx("span", { children: value })] }, key))) }));
}
function renderAdditional(params) {
    return (_jsx("div", { className: "ps-3 pe-3", children: params && Object.entries(params).map(([key, value]) => (_jsxs("div", { children: [_jsxs("span", { className: "fw-bold", children: [key, ": "] }), _jsx("pre", { children: JSON.stringify(value, null, 2).replace(/^"(.*)"$/, "$1") })] }, key))) }));
}
